import { useState, useCallback, useEffect } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { Button } from "@datagame/ui";

import { GET_DEMOGRAPHIC_QUESTIONS } from "../../../graphql/queries/DemographicQuestions";
import { SUBMIT_DEMOGRAPHIC_RESPONSES } from "../../../graphql/mutations/DemographicQuestions";
import { DemographicQuestionRenderer } from "./components";
import { DemographicResponse } from "./types";
import { GameVisuals } from "../../../gql/graphql";

import styles from "../QuizGame.module.scss";

interface DemographicQuestionsProps {
  sessionId: string | null;
  onComplete: () => void;
  gameVisuals: GameVisuals;
  isPreviewMode: boolean;
}

export const DemographicQuestions = ({
  sessionId,
  onComplete,
  gameVisuals,
  isPreviewMode,
}: DemographicQuestionsProps) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState<Map<string, DemographicResponse>>(new Map());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentResponse, setCurrentResponse] = useState<DemographicResponse | null>(null);

  const { data: questionsData, loading } = useQuery(GET_DEMOGRAPHIC_QUESTIONS);
  const [submitResponses] = useMutation(SUBMIT_DEMOGRAPHIC_RESPONSES);

  const questions = questionsData?.getDemographicQuestions || [];
  const currentQuestion = questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === questions.length - 1;

  useEffect(() => {
    if (currentQuestion) {
      const existing = responses.get(currentQuestion.id);
      setCurrentResponse(
        existing || {
          questionId: currentQuestion.id,
          textValue: "",
          numberValue: undefined,
          selectedOptionIds: [],
        },
      );
    }
  }, [currentQuestion, responses]);

  const handleResponseChange = useCallback((response: Partial<DemographicResponse>) => {
    setCurrentResponse((prev) => (prev ? { ...prev, ...response } : null));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    const finalResponses = new Map(responses);
    if (currentResponse && currentQuestion) {
      finalResponses.set(currentQuestion.id, currentResponse);
    }

    if (isPreviewMode || !sessionId) {
      onComplete();
      return;
    }

    try {
      const responsesArray = Array.from(finalResponses.values()).filter((response) => {
        return (
          response.textValue ||
          response.numberValue !== undefined ||
          (response.selectedOptionIds && response.selectedOptionIds.length > 0)
        );
      });

      if (responsesArray.length > 0) {
        await submitResponses({
          variables: {
            sessionId,
            responses: responsesArray,
          },
        });
      }
    } catch (error) {
      console.error("Failed to submit demographic responses:", error);
    } finally {
      setIsSubmitting(false);
      onComplete();
    }
  }, [
    sessionId,
    responses,
    currentResponse,
    currentQuestion,
    submitResponses,
    onComplete,
    isPreviewMode,
    isSubmitting,
  ]);

  const handleSkip = useCallback(() => {
    if (isSubmitting) return;
    onComplete();
  }, [isSubmitting, onComplete]);

  const handleNext = useCallback(() => {
    if (!currentResponse || !currentQuestion) return;

    setResponses((prev) => {
      const newResponses = new Map(prev);
      newResponses.set(currentQuestion.id, currentResponse);
      return newResponses;
    });

    if (isLastQuestion) {
      handleSubmit();
    } else {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  }, [currentResponse, currentQuestion, isLastQuestion, handleSubmit]);

  const handleInputChange = useCallback(
    (response: Partial<DemographicResponse>) => {
      handleResponseChange(response);
    },
    [handleResponseChange],
  );

  const hasResponse = () => {
    if (!currentResponse) return false;

    return (
      currentResponse.textValue ||
      currentResponse.numberValue !== undefined ||
      (currentResponse.selectedOptionIds && currentResponse.selectedOptionIds.length > 0)
    );
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <p>Loading demographic questions...</p>
      </div>
    );
  }

  if (!currentQuestion) {
    return (
      <div className={styles.loadingContainer}>
        <p>No demographic questions available.</p>
      </div>
    );
  }

  return (
    <div className={styles.gameContent}>
      {currentResponse && (
        <DemographicQuestionRenderer
          question={currentQuestion}
          response={currentResponse}
          gameVisuals={gameVisuals}
          onChange={handleInputChange}
          questionIndex={currentQuestionIndex + 1}
          totalQuestions={questions.length}
        />
      )}

      <div className={styles.buttonContainer}>
        <button className={styles.skipButton} onClick={handleSkip} disabled={isSubmitting} type="button">
          Skip and jump to results
        </button>
        <Button
          className={styles.button}
          onClick={handleNext}
          disabled={isSubmitting || (currentQuestion.isRequired && !hasResponse())}
          size="large"
        >
          {isSubmitting ? "Submitting..." : isLastQuestion ? "Continue to Results" : "Next Question"}
        </Button>
      </div>
    </div>
  );
};
